import { useState } from "react";
import { Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/shared/ui/dialog.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Textarea } from "@/shared/ui/textarea.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { useCreateLead } from "../use-leads.js";
import {
  STAGES, TEMPERATURES, TEMPERATURE_LABELS, ORIGIN_SUGGESTIONS, CHANNEL_OPTIONS,
} from "../lead-constants.js";

const VAZIO = {
  name: "",
  phone: "",
  origin: "Meta Ads",
  campaign: "",
  channel: "WhatsApp",
  status: "novo",
  temperature: "warm",
  owner: "",
  procedure: "",
  summary: "",
};

/** Formulário de novo lead — mesmos campos do app atual. */
export function CreateLeadDialog() {
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState(VAZIO);
  const criar = useCreateLead();

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));
  const setInput = (campo) => (evento) => set(campo)(evento.target.value);

  function salvar(evento) {
    evento.preventDefault();
    criar.mutate(form, {
      onSuccess: () => {
        setForm(VAZIO);
        setAberto(false);
      },
    });
  }

  return (
    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          Novo lead
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Novo lead</DialogTitle>
          <DialogDescription>
            Cadastre um contato para acompanhar no funil comercial.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={salvar} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo label="Nome" htmlFor="lead-name" className="sm:col-span-2">
              <Input
                id="lead-name"
                required
                maxLength={120}
                value={form.name}
                onChange={setInput("name")}
                placeholder="Nome completo do lead"
              />
            </Campo>

            <Campo label="Telefone" htmlFor="lead-phone">
              <Input
                id="lead-phone"
                value={form.phone}
                onChange={setInput("phone")}
                placeholder="+55 11 90000-0000"
              />
            </Campo>

            <Campo label="Procedimento" htmlFor="lead-procedure">
              <Input
                id="lead-procedure"
                value={form.procedure}
                onChange={setInput("procedure")}
                placeholder="Implante, Ortodontia..."
              />
            </Campo>

            <Campo label="Origem">
              <Select value={form.origin} onValueChange={set("origin")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORIGIN_SUGGESTIONS.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo label="Campanha" htmlFor="lead-campaign">
              <Input
                id="lead-campaign"
                value={form.campaign}
                onChange={setInput("campaign")}
                placeholder="Nome da campanha"
              />
            </Campo>

            <Campo label="Canal">
              <Select value={form.channel} onValueChange={set("channel")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo label="Responsável" htmlFor="lead-owner">
              <Input
                id="lead-owner"
                value={form.owner}
                onChange={setInput("owner")}
                placeholder="Quem vai atender"
              />
            </Campo>

            <Campo label="Etapa">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo label="Temperatura">
              <Select value={form.temperature} onValueChange={set("temperature")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPERATURES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TEMPERATURE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>

            <Campo label="Resumo" htmlFor="lead-summary" className="sm:col-span-2">
              <Textarea
                id="lead-summary"
                rows={3}
                maxLength={1000}
                value={form.summary}
                onChange={setInput("summary")}
                placeholder="O que o lead procura, o que já foi conversado..."
              />
            </Campo>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={criar.isPending || !form.name.trim()}>
              {criar.isPending ? <Spinner /> : null}
              Criar lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Campo({ label, htmlFor, className, children }) {
  return (
    <div className={`space-y-2 ${className ?? ""}`}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}
